/**
 * Transcript generation module for Fyncall Widget
 * Handles collecting messages and generating downloadable transcripts in text and PDF formats
 */

import { escapeHtml, adjustColor } from '../utils/helpers.js';

/**
 * Collect messages from the message container for transcript generation
 * @param {HTMLElement} messagesContainer - The container element with messages
 * @param {Object} config - Widget configuration
 * @returns {Array<{sender: string, content: string, time: string, isVisitor: boolean}>}
 */
export function collectMessagesForTranscript(messagesContainer, config) {
    const messageElements = messagesContainer.querySelectorAll('.fyncall-message');
    const messages = [];

    messageElements.forEach(msg => {
        const isVisitor = msg.classList.contains('visitor');
        const sender = isVisitor ? 'You' : (config.agentName || 'Support');
        const content = msg.querySelector('.fyncall-message-content')?.textContent?.trim() || '';
        const timeEl = msg.querySelector('.fyncall-message-time');
        const time = timeEl ? timeEl.textContent.trim().split('✓')[0].trim() : '';

        if (content) {
            messages.push({ sender, content, time, isVisitor });
        }
    });

    return messages;
}

/**
 * Generate a plain text transcript
 * @param {Array} messages - Array of message objects
 * @param {Object} config - Widget configuration
 * @returns {string} Plain text transcript
 */
export function generateTextTranscript(messages, config) {
    let transcript = `Chat Transcript\n`;
    transcript += `Date: ${new Date().toLocaleDateString()}\n`;
    transcript += `Time: ${new Date().toLocaleTimeString()}\n`;
    transcript += `${'='.repeat(40)}\n\n`;

    messages.forEach(msg => {
        if (msg.content) {
            transcript += `[${msg.time}] ${msg.sender}:\n${msg.content}\n\n`;
        }
    });

    transcript += `${'='.repeat(40)}\n`;
    transcript += `Generated by ${config.poweredByText || 'Fyncall'}\n`;

    return transcript;
}

/**
 * Download a file with the given content
 * @param {string} content - File content
 * @param {string} filename - Name for the downloaded file
 * @param {string} mimeType - MIME type of the file
 */
export function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/**
 * Download chat transcript as a plain text file
 * @param {HTMLElement} messagesContainer - The container element with messages
 * @param {Object} config - Widget configuration
 * @param {Function} showStatus - Function to show status messages
 */
export function downloadTranscriptText(messagesContainer, config, showStatus) {
    const messages = collectMessagesForTranscript(messagesContainer, config);

    if (!messages.length) {
        showStatus?.('No messages to download', 'error');
        return;
    }

    const transcript = generateTextTranscript(messages, config);
    const filename = `chat-transcript-${new Date().toISOString().split('T')[0]}.txt`;

    downloadFile(transcript, filename, 'text/plain');
    showStatus?.('Transcript downloaded', 'success');
}

/**
 * Generate HTML content for PDF transcript
 * @param {Array} messages - Array of message objects
 * @param {Object} config - Widget configuration
 * @returns {string} HTML content for the PDF
 */
export function generatePDFHtmlContent(messages, config) {
    const companyName = config.companyName || config.title || 'Chat Support';
    const primaryColor = config.primaryColor || '#7c3aed';
    const date = new Date();
    const dateStr = date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
    });

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Chat Transcript - ${companyName}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            background: #f8fafc;
            color: #1e293b;
            line-height: 1.6;
            padding: 40px;
        }
        .container {
            max-width: 700px;
            margin: 0 auto;
            background: white;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
            overflow: hidden;
        }
        .header {
            background: linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, -20)} 100%);
            color: white;
            padding: 32px;
            text-align: center;
        }
        .header-icon {
            width: 60px;
            height: 60px;
            background: rgba(255,255,255,0.2);
            border-radius: 50%;
            margin: 0 auto 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 28px;
        }
        .header h1 {
            font-size: 24px;
            font-weight: 600;
            margin-bottom: 8px;
        }
        .header-meta {
            font-size: 14px;
            opacity: 0.9;
        }
        .content {
            padding: 32px;
        }
        .info-bar {
            display: flex;
            justify-content: space-between;
            padding: 16px 20px;
            background: #f1f5f9;
            border-radius: 12px;
            margin-bottom: 24px;
            font-size: 13px;
            color: #64748b;
        }
        .info-item {
            display: flex;
            align-items: center;
            gap: 8px;
        }
        .messages {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }
        .message {
            display: flex;
            flex-direction: column;
            max-width: 85%;
        }
        .message.visitor {
            align-self: flex-end;
        }
        .message.bot {
            align-self: flex-start;
        }
        .message-sender {
            font-size: 12px;
            font-weight: 600;
            color: #64748b;
            margin-bottom: 4px;
            padding-left: 4px;
        }
        .message.visitor .message-sender {
            text-align: right;
            padding-right: 4px;
        }
        .message-bubble {
            padding: 14px 18px;
            border-radius: 16px;
            font-size: 14px;
            line-height: 1.5;
        }
        .message.visitor .message-bubble {
            background: ${primaryColor};
            color: white;
            border-bottom-right-radius: 4px;
        }
        .message.bot .message-bubble {
            background: #f1f5f9;
            color: #1e293b;
            border-bottom-left-radius: 4px;
        }
        .message-time {
            font-size: 11px;
            color: #94a3b8;
            margin-top: 4px;
            padding-left: 4px;
        }
        .message.visitor .message-time {
            text-align: right;
            padding-right: 4px;
        }
        .footer {
            text-align: center;
            padding: 24px;
            border-top: 1px solid #e2e8f0;
            color: #94a3b8;
            font-size: 12px;
        }
        .footer-logo {
            font-weight: 600;
            color: #64748b;
        }
        @media print {
            body { background: white; padding: 0; }
            .container { box-shadow: none; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="header-icon">💬</div>
            <h1>Chat Transcript</h1>
            <div class="header-meta">${companyName}</div>
        </div>
        <div class="content">
            <div class="info-bar">
                <div class="info-item">
                    <span>📅</span>
                    <span>${dateStr}</span>
                </div>
                <div class="info-item">
                    <span>🕐</span>
                    <span>${timeStr}</span>
                </div>
                <div class="info-item">
                    <span>💬</span>
                    <span>${messages.length} messages</span>
                </div>
            </div>
            <div class="messages">
                ${messages.map(msg => `
                    <div class="message ${msg.isVisitor ? 'visitor' : 'bot'}">
                        <div class="message-sender">${msg.sender}</div>
                        <div class="message-bubble">${escapeHtml(msg.content)}</div>
                        ${msg.time ? `<div class="message-time">${msg.time}</div>` : ''}
                    </div>
                `).join('')}
            </div>
        </div>
        <div class="footer">
            <div class="footer-logo">${config.poweredByText || 'Powered by Fyncall'}</div>
        </div>
    </div>
    <script>
        window.onload = function() {
            window.print();
        };
    </script>
</body>
</html>`;
}

/**
 * Download transcript as a PDF (opens print dialog in new window)
 * @param {Array} messages - Array of message objects (or null to collect from container)
 * @param {HTMLElement} messagesContainer - The container element with messages (fallback)
 * @param {Object} config - Widget configuration
 * @param {Function} showStatus - Function to show status messages
 */
export function downloadTranscriptPDF(messages, messagesContainer, config, showStatus) {
    const messageList = messages || collectMessagesForTranscript(messagesContainer, config);

    if (!messageList.length) {
        showStatus?.('No messages to download', 'error');
        return;
    }

    const htmlContent = generatePDFHtmlContent(messageList, config);

    // Open in new window for printing/saving as PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
}
